import React, {useEffect, useMemo} from "react";
import {Badge} from "antd";
import BookCover from "@components/book-cover";
import {IssueRenderProps} from "@components/grid/IssueGrid";
import {useCollectionCloneIssueMutation, useCollectionRemoveIssuesMutation} from "@mwServices/User";
import {showResponse} from "@services/Api";
import {useNavigate} from "react-router-dom";
import moment from "moment";
import MyCollectionListItemDesktop from "@mwPages/my-collection/issues/MyCollectionListItemDesktop";
import MyCollectionListItemMobile from "@mwPages/my-collection/issues/MyCollectionListItemMobile";

interface MyCollectionListItemProps extends IssueRenderProps {
    onSetBoxImage?: (issueId: number) => void;
    boxId?: number;
    showEditMultiple?: boolean;
    isSelected?: boolean;
    toggleSelected: (collectionId: number) => void;
    setShowMarkAsSoldWithIssue?: (collectionId: number) => void;
    setShowAssignPurchasePriceModalWithIssue?: (collectionId: number) => void;
    showDollarGains?: boolean;
    showDesktopView?: boolean;
    showPriceInfo?: boolean;
    allowUnlimited?:boolean;
    windowDimensions?: any;
    getBoxName: (id: number) => string;
    removeIssueFromBox: (boxId: number, collectionItemId: number) => void;
}

const MyCollectionListItem = ({
                                  item,
                                  onClick,
                                  onSetBoxImage,
                                  boxId,
                                  showEditMultiple,
                                  isSelected,
                                  toggleSelected,
                                  showDollarGains,
                                  showDesktopView,
                                  setShowAssignPurchasePriceModalWithIssue,
                                  setShowMarkAsSoldWithIssue,
                                  showPriceInfo,
                                  allowUnlimited,
                                  windowDimensions,
                                  getBoxName,
                                  removeIssueFromBox,
}: MyCollectionListItemProps) => {
    const issueDate = moment(item.date).toDate();
    const [postRemoveIssues, removeIssuesResponse] = useCollectionRemoveIssuesMutation();
    const [postCloneIssue, cloneIssueResponse] = useCollectionCloneIssueMutation();
    const navigate = useNavigate();

    const notes = useMemo(() => {
        if(!(item.notes || item.purchase_notes || item.purchase_contact_notes || item.review_review || item.grader_notes)) return null;
        return (<>
            {item.notes && (<p><b>Notes:</b><br/> {item.notes}</p>)}
            {item.grader_notes && (<p><b>Grader Notes:</b><br/> {item.grader_notes}</p>)}
            {item.purchase_notes && (<p><b>Purchase Notes:</b><br/> {item.purchase_notes}</p>)}
            {item.purchase_contact_notes && (<p><b>Seller Notes:</b><br/> {item.purchase_contact_notes}</p>)}
            {item.review_review && (<p><b>Review Notes:</b><br/> {item.review_review}</p>)}
        </>);
    }, [item]);

    useEffect(() => {
        showResponse(cloneIssueResponse, 'Issue duplicated.', 'Sorry, there was a problem duplicating the issue from your collection.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cloneIssueResponse]);

    useEffect(() => {
        showResponse(removeIssuesResponse, 'Issues removed from collection.', 'Sorry, there was a problem removing the issues from the collection.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removeIssuesResponse]);

    const bookCover = useMemo(() => (<BookCover issue={item} className="list-book-cover" />),[item]);

    const showMarkAsSold = () => {
        if(setShowMarkAsSoldWithIssue){
            setShowMarkAsSoldWithIssue(item.collection_item_id);
        }
    };

    return (<>
        {showDesktopView ? (
            <MyCollectionListItemDesktop
                item={item}
                showEditMultiple={showEditMultiple}
                isSelected={isSelected}
                toggleSelected={toggleSelected}
                bookCover={bookCover}
                onClick={onClick}
                issueDate={issueDate}
                showPriceInfo={showPriceInfo}
                allowUnlimited={allowUnlimited}
                showDollarGains={showDollarGains}
                notes={notes}
                navigate={navigate}
                postRemoveIssues={postRemoveIssues}
                postCloneIssue={postCloneIssue}
                onSetBoxImage={onSetBoxImage}
                boxId={boxId}
                removeIssueFromBox={removeIssueFromBox}
                getBoxName={getBoxName}
                showMarkAsSold={showMarkAsSold}
                windowDimensions={windowDimensions}
                setShowAssignPurchasePriceModalWithIssue={setShowAssignPurchasePriceModalWithIssue}
            />
        ) : (
            <MyCollectionListItemMobile
                item={item}
                showEditMultiple={showEditMultiple}
                isSelected={isSelected}
                toggleSelected={toggleSelected}
                bookCover={bookCover}
                onClick={onClick}
                issueDate={issueDate}
                showPriceInfo={showPriceInfo}
                allowUnlimited={allowUnlimited}
                showDollarGains={showDollarGains}
                notes={notes}
                navigate={navigate}
                postRemoveIssues={postRemoveIssues}
                postCloneIssue={postCloneIssue}
                onSetBoxImage={onSetBoxImage}
                boxId={boxId}
                removeIssueFromBox={removeIssueFromBox}
                getBoxName={getBoxName}
                showMarkAsSold={showMarkAsSold}
                windowDimensions={windowDimensions}
            />
        )}
    </>);
};

export default MyCollectionListItem;